import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import {
  BRAND_MODAL_PAPER_PROPS,
  BRAND_MODAL_TITLE_SX,
} from "../../../shared/components/modalStyles";
import { formatDurationSeconds } from "../../../utils/formatters";

const BRAND_BLUE = "#00336C";

export default function VisitPageUrlsModal({
  open,
  onClose,
  companyName,
  visitDate,
  pages = [],
  // 🔹 Visit-level duration fallback
  visitDurationSeconds,
}) {
  const safePages = useMemo(
    () => (pages || []).filter((p) => p && p.url),
    [pages],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={BRAND_MODAL_PAPER_PROPS}
    >
      <DialogTitle sx={BRAND_MODAL_TITLE_SX}>
        Page Visits for {companyName || "Unknown Company"}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background:
            "linear-gradient(180deg, rgba(238,244,251,0.8) 0%, rgba(255,255,255,1) 38%)",
          py: 2.5,
        }}
      >
        {visitDate && (
          <Box
            sx={{
              mb: 2,
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              backgroundColor: "rgba(0, 51, 108, 0.08)",
              border: `1px solid rgba(0, 51, 108, 0.16)`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: BRAND_BLUE, fontWeight: 700 }}
            >
              Visit date: {visitDate}
            </Typography>
          </Box>
        )}

        {safePages.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              backgroundColor: "white",
              border: "1px dashed rgba(0, 51, 108, 0.24)",
            }}
          >
            <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>
              No page URLs were recorded for this visit.
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {safePages.map((p, idx) => {
              // 1️⃣ Prefer per-page duration fields
              // 2️⃣ Fallback to visit-level duration passed in
              let durationSeconds = null;

              if (p.duration_seconds != null) {
                durationSeconds = Number(p.duration_seconds);
              } else if (p.durationSeconds != null) {
                durationSeconds = Number(p.durationSeconds);
              } else if (visitDurationSeconds != null) {
                durationSeconds = Number(visitDurationSeconds);
              }

              const prettyDuration =
                durationSeconds != null && durationSeconds > 0
                  ? formatDurationSeconds(durationSeconds)
                  : null;

              return (
                <ListItem
                  key={`${p.url}-${idx}`}
                  disableGutters
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    alignItems: "flex-start",
                    borderRadius: 2,
                    backgroundColor: "white",
                    border: "1px solid rgba(0, 51, 108, 0.12)",
                    boxShadow: "0 6px 18px rgba(0, 51, 108, 0.06)",
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        component="a"
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                          color: BRAND_BLUE,
                          wordBreak: "break-all",
                          fontWeight: 700,
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {p.url}
                      </Typography>
                    }
                    secondary={
                      <Box
                        sx={{
                          mt: 0.25,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {p.last_seen && (
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(0, 51, 108, 0.72)" }}
                          >
                            Last seen:{" "}
                            {dayjs(p.last_seen).format("YYYY-MM-DD HH:mm")}
                          </Typography>
                        )}

                        {/* ✅ Only show chip if duration > 0 */}
                        {prettyDuration && (
                          <Chip
                            size="small"
                            label={`Time on page: ${prettyDuration}`}
                            sx={{
                              alignSelf: "flex-start",
                              backgroundColor: BRAND_BLUE,
                              color: "white",
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: BRAND_BLUE,
            fontWeight: 700,
            borderColor: BRAND_BLUE,
            "&:hover": {
              backgroundColor: "rgba(0, 51, 108, 0.08)",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
